import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { SnackbarService } from '@app/services/snackbar.service';
import { AuthService } from '@auth0/auth0-angular';

export const SILENT_ERROR_CONTEXT = new HttpContextToken(() => false);

@Injectable()
export class SynappsHttpInterceptor implements HttpInterceptor {

    constructor(
        private snackbarService: SnackbarService,
        private authService: AuthService,
    ) { }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Relative urls should default to the SynApps API
        const url = req.url.startsWith('/') ?
            Location.joinWithSlash(environment.url, req.url) :
            req.url;

        if (!url.startsWith(environment.url) && !url.startsWith(environment.cloudFunctionUnprovisioning)) {
            return this.httpHandler(req, next);
        }
        return this.authService.getAccessTokenSilently().pipe(
            mergeMap(token => {
                const headers = {
                    Authorization: 'Bearer ' + token
                };

                const currentCell = localStorage.getItem('currentCell');
                if (currentCell) {
                    headers['x-synapps-cell'] = currentCell;
                }

                req = req.clone({
                    setHeaders: headers,
                    url: url
                });

                return this.httpHandler(req, next);
            })
        );
    }

    private httpHandler(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
        return next
            .handle(req)
            .pipe(
                catchError((err) => {
                    if (err instanceof HttpErrorResponse) {
                        if (err.status === 401) {
                            // JWT expired or is invalid, we force a logout
                            this.authService.logout({
                                logoutParams: {
                                    returnTo: `${window.location.origin}/login`
                                }
                            });

                            return of();
                        }

                        if (!req.context.get(SILENT_ERROR_CONTEXT)) {
                            // Unboxing error
                            const errorBody = err.error && err.error.error;
                            // We check if status is less than 200 to handle edge cases like CORS cancelled requests
                            const errorMessage = err.status < 200 ? err.message :
                                `${errorBody.status || ''} (${errorBody.title || ''}): ${errorBody.detail || ''}`;

                            this.snackbarService.show({
                                message: errorMessage,
                                type: 'danger',
                                timeout: 5000
                            });
                        }
                    }
                    if (err) {
                        throw err;
                    }

                    return of(err);
                }),
            ) as Observable<HttpEvent<any>>;
    }
}
